// FoundHerInnovationHub.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRocket,
  faTools,
  faComments,
  faBoxOpen,
  faHeart,
  faStore,
  faFlask,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "../../../orderflow/hooks/useHerKart";

// Central route constant — adjust here if your router path differs.
const ALL_PRODUCTS_ROUTE = "/herkart/products";

const Portlet = ({ icon, title, desc, expanded, onToggle }) => {
  return (
    <div className="group bg-white/95 backdrop-blur-xl border border-pink-200 rounded-3xl shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full p-6 flex flex-col items-center gap-4 text-left hover:bg-gradient-to-br hover:from-pink-50 hover:to-violet-50 transition-all"
        aria-expanded={expanded}
      >
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-violet-500 flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform">
          <FontAwesomeIcon icon={icon} className="text-3xl" />
        </div>

        <div className="w-full text-center">
          <h3 className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-violet-600 bg-clip-text text-transparent">
            {title}
          </h3>
          <p className="text-violet-700 mt-2 text-sm leading-relaxed">{desc}</p>
        </div>

        <div className="mt-2 px-5 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-pink-500 to-violet-500 text-white flex items-center gap-2">
          {expanded ? "✕ Collapse" : "✨ Explore"}
        </div>
      </button>
    </div>
  );
};

// Dummy sample stores — shown only when "Sample" toggle is active
const sampleInnovationStores = [
  { storeCode: "SAMPLE-ECOWEAVE", storeName: "EcoWeave Sanitary Pouches", storeHeadline: "Biodegradable, stylish packaging co-created with women artisans.", storeImage: "", isSample: true },
  { storeCode: "SAMPLE-SHEFIN", storeName: "SheFin Budgeting App", storeHeadline: "Finance tracker designed by & for women in the gig economy.", storeImage: "", isSample: true },
  { storeCode: "SAMPLE-BLOOMTHREADS", storeName: "Bloom Threads — Sustainable Fashion", storeHeadline: "Handloom wear empowering rural women weavers.", storeImage: "", isSample: true },
];

const InnovativeProducts = () => {
  const navigate = useNavigate();
  const { fetchLiveStores } = useHerKart(); // fetches all active stores, filtered client-side below

  const [liveStores, setLiveStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSamples, setShowSamples] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await fetchLiveStores();
        if (response?.success) {
          const innovationStores = (response.stores || []).filter((s) => s.isInnovationOnly);
          setLiveStores(innovationStores);
        }
      } catch (e) {
        console.error("Stores fetch error:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stores = showSamples ? sampleInnovationStores : liveStores;

  const handleStoreClick = (store) => {
    if (store.isSample) return; // sample stores aren't real — non-clickable
    navigate(`/herkart/products/${store.storeCode}`);
  };

  return (
    <div>
      <div className="mb-4 flex flex-col sm:flex-row justify-end gap-3">
        <button
          onClick={() => navigate(ALL_PRODUCTS_ROUTE)}
          className="flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs font-medium bg-pink-100 text-pink-700 hover:bg-pink-200 transition"
        >
          <FontAwesomeIcon icon={faBoxOpen} />
          Browse All HerKart Products
        </button>
        <button
          onClick={() => setShowSamples((prev) => !prev)}
          className={`flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs font-medium ${
            showSamples ? "bg-amber-100 text-amber-700" : "bg-violet-100 text-violet-700"
          }`}
        >
          <FontAwesomeIcon icon={faFlask} />
          {showSamples ? "Showing Sample — Switch to Live" : "Showing Live — Switch to Sample"}
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-violet-500">Loading stores...</div>
      ) : stores.length === 0 ? (
        <div className="py-10 text-center text-violet-500">
          {showSamples ? "No sample stores available." : "No live innovation stores yet. Check back soon!"}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {stores.map((store) => (
            <div
              key={store.storeCode}
              onClick={() => handleStoreClick(store)}
              className={`bg-white border rounded-3xl p-4 sm:p-5 transition-all group ${
                store.isSample
                  ? "border-dashed border-amber-300 cursor-not-allowed"
                  : "border-pink-100 hover:border-violet-300 cursor-pointer"
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-violet-100">
                  {store.storeImage ? (
                    <img src={store.storeImage} alt={store.storeName} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-violet-400">
                      <FontAwesomeIcon icon={faStore} className="text-2xl" />
                    </div>
                  )}
                </div>

                <div className="min-w-0">
                  <h4 className="font-bold text-base sm:text-lg text-violet-950">{store.storeName}</h4>
                  {store.storeHeadline && (
                    <p className="text-sm text-violet-600 mt-1 line-clamp-2">{store.storeHeadline}</p>
                  )}
                  {store.isSample && (
                    <span className="mt-2 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                      Sample — Not Clickable
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InnovativeServices = () => {
  const services = [
    { id: 1, name: "Sisterhood Mentorship Circle", price: 1500, desc: "1:1 guidance from women who've scaled successfully." },
    { id: 2, name: "Glow-Up Brand Studio", price: 12000, desc: "Beautiful, feminine branding & packaging that sells." },
  ];

  const [requests, setRequests] = useState([]);

  const requestService = (s) => {
    const contact = prompt(`Enter your email/phone for ${s.name}:`);
    if (!contact) return;
    setRequests((prev) => [...prev, { id: Date.now(), service: s.name, contact }]);
    alert("Request received! We'll connect you soon 💖");
  };

  return (
    <div>
      <div className="space-y-4">
        {services.map((s) => (
          <div key={s.id} className="border border-pink-100 bg-white rounded-3xl p-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <h4 className="font-bold text-lg lg:text-xl text-violet-950">{s.name}</h4>
              <p className="text-violet-600 text-xs lg:text-sm mt-2">{s.desc}</p>
            </div>
            <div className="text-right sm:text-left">
              <div className="text-pink-600 font-bold">₹{s.price}</div>
              <button
                onClick={() => requestService(s)}
                disabled
                className="mt-4 w-fit sm:w-auto px-2 py-2 text-sm bg-gradient-to-r from-pink-500 to-violet-500 text-white rounded-2xl font-medium hover:brightness-110"
              >
                Request Now
              </button>
            </div>
          </div>
        ))}
      </div>

      {requests.length > 0 && (
        <div className="mt-6 p-5 bg-gradient-to-br from-pink-50 to-violet-50 rounded-3xl">
          <h5 className="font-semibold mb-3">Your Requests 💌</h5>
          {requests.map((r) => (
            <div key={r.id} className="py-2 text-sm text-violet-700">{r.service} — {r.contact}</div>
          ))}
        </div>
      )}
    </div>
  );
};

const FeedbackTrials = () => {
  const [trials, setTrials] = useState([
    { id: 1, founder: "Ananya Verma", venture: "EcoWeave", summary: "Piloted with 50 women tailors — reduced costs by 18% and felt so empowering!" },
  ]);
  const [form, setForm] = useState({ founder: "", venture: "", summary: "" });

  const submit = (e) => {
    e.preventDefault();
    setTrials((prev) => [...prev, { ...form, id: Date.now() }]);
    setForm({ founder: "", venture: "", summary: "" });
    alert("Thank you for sharing your story! 🌟");
  };

  return (
    <div>
      <form onSubmit={submit} className="bg-gradient-to-br from-pink-50 to-violet-50 p-6 rounded-3xl mb-6">
        <input
          required
          value={form.founder}
          onChange={(e) => setForm({ ...form, founder: e.target.value })}
          placeholder="Your name"
          className="w-full px-5 py-3 rounded-2xl border border-pink-200 focus:border-pink-400 mb-3"
        />
        <input
          required
          value={form.venture}
          onChange={(e) => setForm({ ...form, venture: e.target.value })}
          placeholder="Your venture name"
          className="w-full px-5 py-3 rounded-2xl border border-pink-200 focus:border-pink-400 mb-3"
        />
        <textarea
          required
          value={form.summary}
          onChange={(e) => setForm({ ...form, summary: e.target.value })}
          placeholder="Share your pilot experience..."
          className="w-full px-5 py-4 rounded-3xl border border-pink-200 focus:border-pink-400 h-32 resize-y"
        />
        <button
          type="submit"
          disabled
          className="mt-4 w-fit px-2 py-4 bg-gradient-to-r from-pink-500 to-violet-500 text-white rounded-2xl font-semibold hover:brightness-110 transition"
        >
          Share Your Story ✨
        </button>
      </form>

      <div className="space-y-4">
        {trials.map((t) => (
          <div key={t.id} className="bg-white border border-pink-100 p-6 rounded-3xl">
            <h4 className="font-bold text-lg">{t.venture} — <span className="text-pink-600">{t.founder}</span></h4>
            <p className="mt-3 text-violet-700 italic">"{t.summary}"</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const SampleAsk = () => {
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ name: "", product: "", address: "" });

  const submit = (e) => {
    e.preventDefault();
    setRequests((prev) => [...prev, { ...form, id: Date.now(), status: "Requested 💌" }]);
    setForm({ name: "", product: "", address: "" });
    alert("Pilot kit request sent! We'll keep you updated ✨");
  };

  return (
    <div>
      <form onSubmit={submit} className="space-y-3 mb-6">
        <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Your beautiful name" className="w-full px-5 py-3 rounded-2xl border border-pink-200" />
        <input required value={form.product} onChange={(e) => setForm({...form, product: e.target.value})} placeholder="Product you want to pilot" className="w-full px-5 py-3 rounded-2xl border border-pink-200" />
        <input required value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} placeholder="Delivery address" className="w-full px-5 py-3 rounded-2xl border border-pink-200" />
        <button disabled type="submit" className="w-fit px-2 py-2 bg-gradient-to-r from-pink-500 to-violet-500 text-white rounded-2xl font-semibold">Request Pilot Kit 🌸</button>
      </form>

      {requests.length > 0 && (
        <div className="space-y-3">
          {requests.map(r => (
            <div key={r.id} className="p-4 bg-white border border-pink-100 rounded-2xl text-sm flex justify-between items-center">
              <div>{r.product} • {r.name}</div>
              <div className="text-pink-500 font-medium">{r.status}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------- Main Component ------------------- */
export default function FoundHerInnovationHub() {
  const [active, setActive] = useState(null);

  const useCases = [
    { id: "products", title: "Innovative Products", desc: "Fresh launches by young women entrepreneurs", icon: faRocket, component: <InnovativeProducts /> },
    { id: "services", title: "Support Services", desc: "Mentorship, design & more", icon: faTools, component: <InnovativeServices /> },
    { id: "feedback", title: "Founder Stories", desc: "Real pilot wins & learnings", icon: faComments, component: <FeedbackTrials /> },
    { id: "samples", title: "Pilot Kits", desc: "Try before you scale", icon: faBoxOpen, component: <SampleAsk /> },
  ];

  const activeUseCase = useCases.find((u) => u.id === active);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff0f5] via-[#f3e8ff] to-[#e0f2fe]">
      <section className="py-6 px-4 md:px-4 lg:px-6 max-w-7xl mx-auto">
        <header className="text-center mb-4 bg-transparent">
          <div
            className="inline-flex items-center gap-3 mb-4 px-5 py-3 rounded-2xl
                bg-gradient-to-r from-pink-50 via-violet-50 to-fuchsia-50
                border border-pink-200 shadow-sm"
          >
            <span className="text-xl lg:text-4xl">🌸</span>
            <h1 className="text-xl lg:text-4xl md:text-4xl font-black bg-gradient-to-r from-pink-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent tracking-tighter">
              FoundHer Youth Innovations
            </h1>
          </div>
          <p className="max-w-2xl mx-auto text-sm lg:text-lg text-violet-400 font-light">
            A vibrant space for young women innovators to connect, create, and scale together.
          </p>
        </header>

        {/* Use case selector cards - always uniform, never resize */}
        <div className="grid gap-2 grid-cols-2 lg:grid-cols-4">
          {useCases.map((u) => (
            <Portlet
              key={u.id}
              icon={u.icon}
              title={u.title}
              desc={u.desc}
              expanded={active === u.id}
              onToggle={() => setActive((prev) => (prev === u.id ? null : u.id))}
            />
          ))}
        </div>

        {/* Expanded content - rendered separately below the grid, always full width */}
        {activeUseCase && (
          <div className="mt-6 bg-white/95 backdrop-blur-xl border border-pink-200 rounded-3xl shadow-xl p-6 md:p-8 animate-[fadeIn_0.3s_ease-in-out]">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold bg-gradient-to-r from-pink-600 to-violet-600 bg-clip-text text-transparent">
                {activeUseCase.title}
              </h3>
              <button
                onClick={() => setActive(null)}
                className="w-fit px-5 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-pink-500 to-violet-500 text-white flex items-center gap-2"
              >
                ✕ Collapse
              </button>
            </div>
            {activeUseCase.component}
          </div>
        )}
      </section>

      <footer className="bg-violet-950 text-white/90 py-12 px-6 text-center">
        <p>© 2026 FoundHer • Built with love for women who build</p>
        <div className="flex justify-center gap-8 mt-6 text-sm">
          <Link to="/about-us" className="hover:text-pink-400 transition">About</Link>
          <Link to="/privacy-policy" className="hover:text-pink-400 transition">Privacy</Link>
          <Link to="/terms-of-use" className="hover:text-pink-400 transition">Terms</Link>
          <Link to="/contact-us" className="hover:text-pink-400 transition">Contact</Link>
        </div>
      </footer>
    </div>
  );
}